import * as React from 'react';
import {FC, useEffect, useState} from 'react';
import {Loader, Table} from '@flexe/ui-components';
import {get} from 'lodash';
import Filters from '../shared-v2/Filters';
import {Filter, FilterType, FilterValue} from '../../shared/CommonInterfaces';
import OutboundOrdersService from './OutboundOrdersService';
import {PageSize} from './generic/PageSizeInput';
import {OrderLine} from './OutboundOrdersInterfaces';
import {extractNewFilters, FilterInfo, getInputFilterValue, initialFilters} from './helpers/OutboundOrdersFilters';
import {buildRow, buildTableHeaders} from './OutboundOrderLinesTableRow';
import {OrdersPageError} from './OutboundOrdersV2';

interface Props {
  outboundOrdersService: OutboundOrdersService;
  pageSize: PageSize;
  setErrors: (v: OrdersPageError) => void;
}

interface LinesTableInfo {
  continuationTokens: string[];
  lines: OrderLine[];
  checkedLineIds: {[index: string]: boolean};
  totalCount: number;
  filters: FilterInfo;
  currentPage: number;
}
const initialLinesTableInfo: LinesTableInfo = {
  continuationTokens: [],
  lines: [],
  checkedLineIds: {},
  totalCount: 0,
  filters: initialFilters,
  currentPage: 1
};

const buildFilters = (linesTableInfo: LinesTableInfo): Filter[] => {
  const inputFilters = linesTableInfo.filters.inputFilters;
  return [
    {
      displayName: 'External Line ID',
      key: 'externalIds',
      type: FilterType.String,
      value: getInputFilterValue(inputFilters, 'externalIds'),
      allowMultiple: true
    },
    {
      displayName: 'FLEXE Line ID',
      key: 'lineIds',
      type: FilterType.String,
      value: getInputFilterValue(inputFilters, 'ids'),
      allowMultiple: true
    },
    {
      displayName: 'SKU',
      key: 'skus',
      type: FilterType.String,
      value: getInputFilterValue(inputFilters, 'skus'),
      allowMultiple: true
    },
    {
      displayName: 'FLEXE Order ID',
      key: 'orderIds',
      type: FilterType.String,
      value: getInputFilterValue(inputFilters, 'orderIds'),
      allowMultiple: true
    },
    {
      displayName: 'Issues Only',
      key: 'hasErrors',
      type: FilterType.Present,
      value: getInputFilterValue(inputFilters, 'hasErrors')
    },
    {
      displayName: 'Labels',
      key: 'labels',
      type: FilterType.LabelSearch,
      allowMultiple: true,
      value: getInputFilterValue(inputFilters, 'labels'),
      defaultKey: ''
    }
  ];
};

const getPageOfLines = async (
  pageSize: PageSize,
  setErrors: (v: OrdersPageError) => void,
  outboundOrdersService: OutboundOrdersService,
  currentLinesTableInfo: LinesTableInfo
): Promise<LinesTableInfo> => {
  const newLinesTableInfo: LinesTableInfo = {...currentLinesTableInfo};

  if (!outboundOrdersService) {
    setErrors({errorType: 'UnexpectedFailure'});
    return newLinesTableInfo;
  }

  const error: OrdersPageError = {errorType: 'None'};
  const currentPage = newLinesTableInfo.currentPage;
  const response = await outboundOrdersService.getLines(
    newLinesTableInfo.filters.apiFilters,
    currentPage > 1 ? newLinesTableInfo.continuationTokens[currentPage - 2] : null,
    pageSize,
    null,
    {returnAllErrors: true}
  );

  if (!response) {
    newLinesTableInfo.lines = [];
    newLinesTableInfo.totalCount = 0;

    error.errorType = 'UnexpectedFailure';
  } else if (response.errors && response.errors.length > 0) {
    newLinesTableInfo.lines = [];
    newLinesTableInfo.totalCount = 0;

    error.errorType = 'ApiResponse';
    error.details = outboundOrdersService.processErrors(response.errors);
  } else {
    const newContinuationTokens: string[] = newLinesTableInfo.continuationTokens;
    // TODO this returning an array is bizarre
    const newToken = [get(response, 'continuationToken')][0];
    if (currentPage > newContinuationTokens.length) {
      newContinuationTokens.push(newToken);
    } else {
      newContinuationTokens[currentPage - 1] = newToken;
    }
    newLinesTableInfo.continuationTokens = newContinuationTokens;

    const tempLines = get(response, 'lines') || ([] as OrderLine[]);
    newLinesTableInfo.lines = outboundOrdersService.assignOrderLineState(tempLines);
    newLinesTableInfo.totalCount = get(response, 'total');
  }

  setErrors(error);
  return newLinesTableInfo;
};

const buildLinesRows = (
  linesTableInfo: LinesTableInfo,
  onToggleOneLineChecked: (event: React.ChangeEvent<HTMLInputElement>, lineId: string) => void
): any[][] => {
  if (!linesTableInfo || !linesTableInfo.lines) {
    return [];
  }

  const onClickOrderLineId = () => {
    // TODO support line modal. This will probably live elsewhere
  };

  return linesTableInfo.lines.map((line: OrderLine) =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    buildRow(line, linesTableInfo.checkedLineIds, onToggleOneLineChecked, onClickOrderLineId)
  );
};

const OutboundOrderLinesTable: FC<Props> = (props) => {
  const [linesTableInfo, setLinesTableInfo] = useState<LinesTableInfo>(initialLinesTableInfo);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Since pageSize is required, this handles initial load as well
  useEffect(() => {
    // Make sure we keep filters when changing page size
    const newLinesTableInfo: LinesTableInfo = {...initialLinesTableInfo, filters: linesTableInfo.filters};
    loadLines(newLinesTableInfo);
  }, [props.pageSize]);

  const handleFilterChange = (newFilters: FilterValue[]) => {
    // Changing a filter means we have to start fresh
    const newLinesTableInfo: LinesTableInfo = {...initialLinesTableInfo, filters: extractNewFilters(newFilters)};
    loadLines(newLinesTableInfo);
  };

  const changePage = (newPage: number) => {
    // Page changes mean we can keep all the current data, but load a different page
    const newOrdersTableInfo: LinesTableInfo = {...initialLinesTableInfo, currentPage: newPage};
    loadLines(newOrdersTableInfo);
  };

  const loadLines = (newLinesTableInfo: LinesTableInfo) => {
    setIsLoading(true);

    getPageOfLines(props.pageSize, props.setErrors, props.outboundOrdersService, newLinesTableInfo)
      .then((result) => {
        setLinesTableInfo(result);
        setIsLoading(false);
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch((_) => {
        // TODO Display the error and maybe add a retry button too (so you don't have to reload and start over)
        setLinesTableInfo(newLinesTableInfo); // TODO can likely remove. Just be safe for now
        setIsLoading(false);
      });
  };

  const onToggleLinesChecked = (event: React.ChangeEvent<HTMLInputElement>, lineId?: string) => {
    if (!event || !event.target) {
      return;
    }

    const newCheckedLineIds = {...linesTableInfo.checkedLineIds};
    const isChecked = event.target.checked;
    if (lineId) {
      newCheckedLineIds[lineId] = isChecked;
    } else {
      linesTableInfo.lines.forEach((line: OrderLine) => (newCheckedLineIds[line.id] = isChecked));
    }
    setLinesTableInfo({...linesTableInfo, checkedLineIds: newCheckedLineIds});
  };
  const onToggleAllLinesChecked = (event: React.ChangeEvent<HTMLInputElement>) => onToggleLinesChecked(event);
  const onToggleOneLineChecked = (event: React.ChangeEvent<HTMLInputElement>, lineId: string) =>
    onToggleLinesChecked(event, lineId);

  if (isLoading) {
    return <Loader loading={isLoading} />;
  }

  const hasUncheckedLine = linesTableInfo.lines.some((l) => !linesTableInfo.checkedLineIds[l.id]);

  return (
    <>
      <div className="row filters">
        <Filters
          filters={buildFilters(linesTableInfo)}
          filterChangeHandler={(newFilters) => handleFilterChange(newFilters)}
        />
      </div>
      {linesTableInfo.lines.length === 0 ? (
        <p>No order lines matching the current filters were found.</p>
      ) : (
        <Table
          tableClass="table-striped"
          tableData={{
            headers: buildTableHeaders(
              linesTableInfo.currentPage,
              linesTableInfo.totalCount,
              hasUncheckedLine,
              props.pageSize,
              changePage,
              onToggleAllLinesChecked
            ),
            rows: buildLinesRows(linesTableInfo, onToggleOneLineChecked)
          }}
        />
      )}
    </>
  );
};
export default OutboundOrderLinesTable;
